import { useCallback, useRef } from 'react';

import {
  ApolloClient,
  FetchResult,
  TypedDocumentNode,
  useApolloClient,
  useMutation,
} from '@apollo/client';
import { Modifiers, StoreObject } from '@apollo/client/cache';

type DataWithEditVersion<T> = T & { editVersion: number };
type WithId<T> = T & { id: any };

export type EditVersionMutationOptions<Input, Output> = {
  cacheObject: WithId<StoreObject>;
  onUpdateCache: (pending: Input) => Exclude<Modifiers, 'editVersion'>;
  onComplete: (response: FetchResult<Output>) => { editVersion: number };
  onError: (
    cacheId: string | undefined,
    client: ApolloClient<object>,
    error: Error,
  ) => Promise<void>;
};

/**
 * use this hook if you want to optimistically update apollo cache, but the
 * mutations are editVersion locked in the backend i.e. each time a mutation is
 * made it must finish before the next can be issued.
 *
 * @example
 * ```
 *   const options: EditVersionMutationOptions<
 *     UpdateInstanceInput,
 *     UpdateInstanceMutation
 *   > = useMemo(
 *     () => ({
 *       cacheObject: { __typename: 'ProtocolInstance', id },
 *       onUpdateCache: pending => ({ name: prevName => pending.name ??prevName }),
 *       onComplete: response => ({ editVersion: response.data?.editVersion ?? 0 }),
 *       onError: async (cacheId, client, _error) => {
 *         client.cache.evict({ id: cacheId });
 *         client.cache.gc();
 *         await client.reFetchObservableQueries();
 *       },
 *     }),
 *     [id],
 *   );
 *
 *  const { handleMutation, loading } = useOptimisticEditVersionMutation(
 *    UPDATE_INSTANCE,
 *    options,
 *  );
 * ```
 */
export function useOptimisticEditVersionMutation<
  Input extends Record<string, any>,
  Output extends any,
>(
  mutation: TypedDocumentNode<Output, { input: DataWithEditVersion<WithId<Input>> }>,
  options: EditVersionMutationOptions<Input, Output>,
) {
  // we can't freely call mutations. So we hold any pending updates and
  // immediately commit them to apollo cache for a responsive UI, and when any
  // in-flight mutations are complete save the pending updates
  const pendingUpdate = useRef<Input>();
  const latestVersion = useRef<number>(0);
  // we don't want loading memorised in handleMutation otherwise two calls of
  // handleMutation could result in concurrent updates, which is unexpected. So
  // hold a ref instead
  const loading = useRef(false);

  const client = useApolloClient();
  const [onMutate] = useMutation(mutation);
  const handleMutation = useCallback(
    async (update: DataWithEditVersion<Input>) => {
      const { cacheObject, onUpdateCache, onComplete, onError } = options;

      const cacheId = client.cache.identify(cacheObject);
      client.cache.modify({
        id: cacheId,
        fields: onUpdateCache(update),
      });

      if (loading.current) {
        pendingUpdate.current = { ...(pendingUpdate.current ?? {}), ...update };
        return;
      }

      try {
        loading.current = true;
        const editVersion = latestVersion.current || update.editVersion;
        const input = { ...update, editVersion, id: cacheObject.id };
        const result = await onMutate({ variables: { input } });
        const { editVersion: nextVersion } = onComplete(result);
        latestVersion.current = nextVersion;
      } catch (error) {
        await onError(cacheId, client, error);
        // clear state so we don't send stale data
        pendingUpdate.current = undefined;
        latestVersion.current = 0;
      } finally {
        loading.current = false;

        if (pendingUpdate.current) {
          const latestUpdate = pendingUpdate.current;
          pendingUpdate.current = undefined;
          await handleMutation({ ...latestUpdate, editVersion: latestVersion.current });
        }
      }
    },
    [client, onMutate, options],
  );

  return { handleMutation, loading: loading.current };
}
